import React, { useCallback } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import {
  toMultiDropdownOptions,
  toNestedDropdownOptions,
} from 'shared/utils/formUtils';
import Api from '../../lib/api';
import { withLocalePrefix } from 'shared/utils/translation';
import { TertiaryButton } from '@grnhse/seedling/lib/birch/components/buttons';
import FieldObject from './field_object';
import CustomField from './custom_field';
import { Stack } from '@grnhse/seedling/lib/birch/layouts/stack';
import { mapWithValueType, filterByTerm } from '../../lib/utils';
import { Item } from '@grnhse/seedling/lib/birch/components/forms/dropdown/item';
import { useFormContext } from '@grnhse/seedling/lib/birch/components/forms/form';
import { ButtonWrapper } from '../../shared';

const t = withLocalePrefix('talent_search.form.sections.candidate.labels');

type Props = {
  apiClient: Api;
  currencies: Item[];
};

const FilteredFields = ['Application', 'Offer'];

const CustomFields = ({ apiClient, currencies }: Props) => {
  const { watch, setValue } = useFormContext();
  const userDateFormat = watch('user_date_format');
  const customFields: FieldObject[] = watch('custom_fields');
  const queryClient = useQueryClient();

  const onAddField = () => {
    setValue('custom_fields', [
      ...customFields,
      FieldObject.emptyField(userDateFormat),
    ]);
  };

  const onRemoveField = (index: number) => {
    customFields.splice(index, 1);
    setValue('custom_fields', customFields, { shouldDirty: true });
  };

  const onCustomFieldChange = (item: Item | null, index: number) => {
    const fieldObject = customFields[index];
    fieldObject.setCustomField(item);
    customFields.splice(index, 1, fieldObject);
    setValue('custom_fields', customFields, { shouldDirty: true });
  };

  const fetchCustomFields = useCallback(
    async (term: string) => {
      const queryKey = ['customFields', term];

      const cachedData: Item[] | null | undefined =
        queryClient.getQueryData(queryKey);

      if (cachedData) {
        return { items: cachedData };
      }

      const items = await queryClient.fetchQuery({
        queryKey,
        queryFn: async () => {
          const fetchedFields = await apiClient.fetchCustomFields(term);
          const filteredFields = fetchedFields.filter(
            (field) => !FilteredFields.includes(field.text)
          );
          const fieldsWithType = mapWithValueType(filteredFields);
          return toNestedDropdownOptions(fieldsWithType);
        },
      });

      return { items };
    },
    [apiClient, queryClient]
  );

  const fetchCustomFieldOptions = useCallback(
    async (id: string, term: string) => {
      const queryKey = ['customFieldOptions', id];

      const cachedData: Item[] | null | undefined =
        queryClient.getQueryData(queryKey);

      if (cachedData) {
        return { items: filterByTerm(cachedData, term) };
      }

      const items = await queryClient.fetchQuery({
        queryKey,
        queryFn: async () => {
          const options = await apiClient.fetchCustomFieldOptions(id);
          return toMultiDropdownOptions(options);
        },
      });

      return { items: filterByTerm(items, term) };
    },
    [apiClient, queryClient]
  );

  return (
    <Stack spacing={2} sx={{ paddingTop: customFields.length > 0 ? 1 : 0 }}>
      {customFields.map((field, index) => (
        <CustomField
          key={field.reactKey}
          fieldObject={field}
          fetchFields={fetchCustomFields}
          fetchOptions={fetchCustomFieldOptions}
          onCustomFieldChange={onCustomFieldChange}
          currencies={currencies}
          remove={() => onRemoveField(index)}
          index={index}
        />
      ))}
      <ButtonWrapper>
        <TertiaryButton
          data-provides={'talent-search-add-custom-field-button'}
          onClick={onAddField}
        >
          {t('add_custom_field')}
        </TertiaryButton>
      </ButtonWrapper>
    </Stack>
  );
};

export default CustomFields;
